import React, { useEffect, useState } from 'react';
import {
  Modal, Form, Input, Select, Button, message, Tag, Card, Avatar, Pagination,
} from 'antd';
import {
  PlusOutlined, ArrowRightOutlined, MessageOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { employeesApi, skillsApi, toolsApi, mcpServersApi, documentsApi, expertsApi, dictionariesApi, uploadApi } from '../services/api-services';
import { resolveUrl } from '../utils/url';
import ImagePicker from '../components/ImagePicker';

const { TextArea } = Input;

const EmployeeList: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);
  const [form] = Form.useForm();

  // Dropdown data
  const [skills, setSkills] = useState<any[]>([]);
  const [tools, setTools] = useState<any[]>([]);
  const [mcpServers, setMcpServers] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [experts, setExperts] = useState<any[]>([]);
  const [employeeTypeOptions, setEmployeeTypeOptions] = useState<{ label: string; value: string }[]>([]);
  const [employeeCategoryOptions, setEmployeeCategoryOptions] = useState<{ label: string; value: string }[]>([]);
  const [acpBackendOptions, setAcpBackendOptions] = useState<{ label: string; value: string }[]>([
    { value: 'hermes', label: 'Hermes' },
    { value: 'kiro', label: 'Kiro CLI' },
    { value: 'copilot', label: 'GitHub Copilot' },
  ]);
  const [activeCategory, setActiveCategory] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const filteredData = activeCategory ? data.filter((item: any) => item.tags?.includes(activeCategory)) : data;
  const pagedData = filteredData.slice((page - 1) * pageSize, page * pageSize);

  const fetchDropdowns = async () => {
    try {
      const [s, t, m, d, e] = await Promise.all([
        skillsApi.list() as any,
        toolsApi.list() as any,
        mcpServersApi.list() as any,
        documentsApi.list() as any,
        expertsApi.list() as any,
      ]);
      setSkills(s);
      setTools(t);
      setMcpServers(m);
      setDocuments(d);
      setExperts(e);
    } catch {
      // silently fail dropdowns
    }
    try {
      const dict: any = await dictionariesApi.getByCode('employee_type');
      if (dict && dict.items) {
        setEmployeeTypeOptions(dict.items.map((item: any) => ({ label: item.name, value: item.value })));
      }
    } catch {
      setEmployeeTypeOptions([
        { label: 'Manager', value: 'manager' },
        { label: 'Technical', value: 'technical' },
      ]);
    }
    try {
      const dict: any = await dictionariesApi.getByCode('employee_category');
      if (dict?.items) setEmployeeCategoryOptions(dict.items.map((i: any) => ({ label: i.name, value: i.value })));
    } catch { /* use free input */ }
    try {
      const dict: any = await dictionariesApi.getByCode('acp_backend');
      if (dict?.items) setAcpBackendOptions(dict.items.map((i: any) => ({ label: i.name, value: i.value })));
    } catch { /* keep defaults */ }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res: any = await employeesApi.list();
      setData(res);
    } catch (e) { message.error('获取数字人列表失败'); }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    fetchDropdowns();
    const onUpdated = () => fetchData();
    window.addEventListener('employees-updated', onUpdated);
    return () => window.removeEventListener('employees-updated', onUpdated);
  }, []);

  const handleSave = async (values: any) => {
    try {
      const payload = { ...values };
      if (editingItem) {
        await employeesApi.update(editingItem.id, payload);
        message.success('更新成功');
      } else {
        await employeesApi.create(payload);
        message.success('创建成功');
      }
      setModalVisible(false);
      form.resetFields();
      setEditingItem(null);
      fetchData();
    } catch (e: any) { message.error(e.response?.data?.detail || '操作失败'); }
  };

  const openCreateModal = () => {
    setEditingItem(null);
    form.resetFields();
    setModalVisible(true);
  };

  const employeeType = Form.useWatch('employee_type', form);

  return (
    <div className="apple-page">
      <div className="apple-page-header">
        <h1 className="apple-page-title">数字人</h1>
        <Button className="apple-btn-create" icon={<PlusOutlined />} onClick={openCreateModal}>新建数字人</Button>
      </div>
      <p className="apple-page-subtitle">创建和管理数字人，配置其技能、工具和关联资源。</p>

      {employeeCategoryOptions.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          <Tag
            style={{ cursor: 'pointer', padding: '4px 12px', fontSize: 13 }}
            color={activeCategory === '' ? 'blue' : undefined}
            onClick={() => { setActiveCategory(''); setPage(1); }}
          >全部</Tag>
          {employeeCategoryOptions.map((opt) => (
            <Tag
              key={opt.value}
              style={{ cursor: 'pointer', padding: '4px 12px', fontSize: 13 }}
              color={activeCategory === opt.value ? 'blue' : undefined}
              onClick={() => { setActiveCategory(activeCategory === opt.value ? '' : opt.value); setPage(1); }}
            >{opt.label}</Tag>
          ))}
        </div>
      )}

      <div className="employee-card-grid">
        {pagedData.map((item: any) => (
          <div key={item.id}>
            <Card
              hoverable
              loading={loading}
              className="employee-card"
              cover={
                <div className="employee-card-header">
                  <Avatar
                    size={58}
                    src={resolveUrl(item.avatar)}
                    className="employee-avatar"
                  >
                    {item.name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                  <div className="employee-card-heading">
                    <h3 className="employee-card-title">{item.name}</h3>
                    <p className="employee-card-subtitle">{item.identifier}</p>
                  </div>
                </div>
              }
              actions={[
                <Button
                  key="chat"
                  type="link"
                  size="small"
                  icon={<MessageOutlined />}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (item.install_status !== 'installed') {
                      message.warning('请先安装数字人后再对话');
                      return;
                    }
                    navigate(`/employees/${item.id}/chat`);
                  }}
                >
                  对话
                </Button>,
                <Button
                  key="detail"
                  type="link"
                  size="small"
                  icon={<ArrowRightOutlined />}
                  onClick={(e) => { e.stopPropagation(); navigate(`/employees/${item.id}`); }}
                >
                  详情
                </Button>,
              ]}
            >
              <Card.Meta
                description={
                  <div className="employee-card-body">
                    <div className="employee-card-tags">
                      {item.tags && item.tags.length > 0
                        ? item.tags.slice(0, 3).map((t: string) => (
                            <Tag key={t} color="blue">
                              {t}
                            </Tag>
                          ))
                        : <span className="employee-card-empty-tags">暂无标签</span>
                      }
                      {item.tags && item.tags.length > 3 && (
                        <Tag>+{item.tags.length - 3}</Tag>
                      )}
                    </div>
                    <p className="employee-card-desc" style={{ 
                      overflow: 'hidden', 
                      textOverflow: 'ellipsis', 
                      display: '-webkit-box', 
                      WebkitLineClamp: 2, 
                      WebkitBoxOrient: 'vertical' 
                    }}>
                      {item.description || '暂无描述'}
                    </p>
                  </div>
                }
              />
            </Card>
          </div>
        ))}
      </div>

      {filteredData.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 24 }}>
          <Pagination current={page} pageSize={pageSize} total={filteredData.length} onChange={(p) => setPage(p)} showSizeChanger pageSizeOptions={['10', '20', '50', '100']} onShowSizeChange={(_, size) => { setPageSize(size); setPage(1); }} showTotal={(t) => `共 ${t} 条`} />
        </div>
      )}

      {filteredData.length === 0 && !loading && (
        <div className="apple-empty">
          <p className="apple-empty-title">暂无数字人</p>
          <p className="apple-empty-desc">点击"新建数字人"开始创建</p>
        </div>
      )}

      {/* Create / Edit Modal */}
      <Modal
        className="apple-modal"
        title={editingItem ? '编辑数字人' : '新建数字人'}
        open={modalVisible}
        onCancel={() => { setModalVisible(false); form.resetFields(); setEditingItem(null); }}
        onOk={() => form.submit()}
        width={800}
        destroyOnHidden
      >
        <Form className="apple-form" form={form} layout="vertical" onFinish={handleSave}>
          <Form.Item name="avatar" label="头像">
            <ImagePicker shape="circle" />
          </Form.Item>
          <Form.Item name="name" label="名称" rules={[{ required: true, message: '请输入名称' }]}>
            <Input placeholder="数字人名称" />
          </Form.Item>
          <Form.Item name="employee_type" label="类型" rules={[{ required: true, message: '请选择类型' }]} initialValue="technical">
            <Select options={employeeTypeOptions} placeholder="请选择员工类型" />
          </Form.Item>
          <Form.Item name="acp_backend" label="ACP 后端" initialValue="hermes">
            <Select options={acpBackendOptions} />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <TextArea rows={3} placeholder="描述数字人的职责和能力" />
          </Form.Item>
          <Form.Item name="tags" label="分类标签">
            <Select mode="multiple" placeholder="选择或输入分类标签" options={employeeCategoryOptions} allowClear />
          </Form.Item>

          {/* team_members - only visible for manager type */}
          {editingItem && employeeType === 'manager' && (
            <Form.Item name="team_members" label="团队成员">
              <Select
                mode="multiple"
                placeholder="选择团队成员"
                options={data
                  .filter((e: any) => e.employee_type === 'technical')
                  .map((e: any) => ({ label: `${e.name} (${e.identifier})`, value: e.id }))}
              />
            </Form.Item>
          )}

          {/* Resource Associations - only shown when editing */}
          {editingItem && (
            <>
              <Form.Item name="skill_ids" label="关联 Skills">
                <Select
                  mode="multiple"
                  placeholder="选择关联的 Skill"
                  options={skills.map((s: any) => ({ label: s.title, value: s.id }))}
                />
              </Form.Item>
              <Form.Item name="tool_ids" label="关联 Tools">
                <Select
                  mode="multiple"
                  placeholder="选择关联的 Tool"
                  options={tools.map((t: any) => ({ label: t.title, value: t.id }))}
                />
              </Form.Item>
              <Form.Item name="mcp_server_ids" label="关联 MCP Servers">
                <Select
                  mode="multiple"
                  placeholder="选择关联的 MCP Server"
                  options={mcpServers.map((m: any) => ({ label: m.title, value: m.id }))}
                />
              </Form.Item>
              <Form.Item name="document_ids" label="关联 Documents">
                <Select
                  mode="multiple"
                  placeholder="选择关联的 Document"
                  options={documents.map((d: any) => ({ label: d.name, value: d.id }))}
                />
              </Form.Item>
              <Form.Item name="expert_ids" label="关联 Experts">
                <Select
                  mode="multiple"
                  placeholder="选择关联的 Expert"
                  options={experts.map((e: any) => ({ label: e.name, value: e.id }))}
                />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default EmployeeList;
